import { CalendarIcon } from '@heroicons/react/outline';
import classNames from 'classnames';

import { MailSequenceEnrollmentStatus } from '../../../../generated/graphql';
import { getShortDate } from '../../../lib/time';
import { pluralize } from '../../../lib/copy';
import {
  ENROLLMENT_STATUS_LABEL,
  sequenceScheduleTiming,
} from '../../../lib/mailSequence';

interface Props {
  label: string;
  referenceDate: string;
  scheduledDates: string[];
  mailCount: number;
  status: MailSequenceEnrollmentStatus;
  isSelected: boolean;
  onClick: () => void;
}

/** Match Activities tab status pills: colored bg + neutral text. */
const ENROLLMENT_PILL: Record<MailSequenceEnrollmentStatus, string> = {
  [MailSequenceEnrollmentStatus.Active]: 'bg-blue-25',
  [MailSequenceEnrollmentStatus.Completed]: 'bg-green-25',
  [MailSequenceEnrollmentStatus.Cancelled]: 'bg-neutral-50',
};

const SequenceListItem: React.FC<Props> = ({
  label,
  referenceDate,
  scheduledDates,
  mailCount,
  status,
  isSelected,
  onClick,
}) => {
  const isActive = status === MailSequenceEnrollmentStatus.Active;
  const isCancelled = status === MailSequenceEnrollmentStatus.Cancelled;
  const timing = sequenceScheduleTiming(referenceDate, scheduledDates);
  const mailStepsLabel = pluralize(mailCount, 'Mail step', 'Mail steps');
  const timingLabel = `${timing} ${getShortDate(referenceDate)}`;

  return (
    <div
      className={classNames(
        'mb-1.5 flex w-full shrink-0 cursor-pointer flex-row items-start justify-between gap-x-2 rounded-md py-3 pl-3 pr-3 hover:bg-neutral-25',
        // Use secondary highlight so status pills (blue/green/neutral) stay visible.
        isSelected && 'bg-secondary-20 font-medium hover:bg-secondary-25',
      )}
      onClick={onClick}
    >
      <div className="flex min-w-0 flex-1 flex-row items-start gap-x-2.5">
        <CalendarIcon
          className={classNames('mt-0.5 h-4 w-4 shrink-0', {
            'text-blue-100': isActive,
            'text-secondary-100':
              status === MailSequenceEnrollmentStatus.Completed,
            'text-neutral-110': isCancelled,
          })}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="text-caption font-medium leading-tight text-neutral-150 line-clamp-2">
            {label}
          </div>
          <div className="pt-1 text-small-label text-neutral-125">
            <div className="truncate">{mailStepsLabel} scheduled</div>
            <div className="truncate">{timingLabel}</div>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            <span
              className={classNames(
                'inline-flex items-center justify-center rounded-full px-1.5 py-px text-small-caption text-neutral-150',
                ENROLLMENT_PILL[status],
              )}
            >
              {ENROLLMENT_STATUS_LABEL[status]}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SequenceListItem;
